import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { scrollToSection } from '../utils/scrollUtils';
import { useNavigate, useLocation } from 'react-router-dom';

interface HeaderMobileProps {
  language: 'fr' | 'en' | 'ar';
  setLanguage: (lang: 'fr' | 'en' | 'ar') => void;
  t: any;
  isRTL: boolean;
  showOnlyHome?: boolean;
  searchTerm?: string;
  setSearchTerm?: (term: string) => void;
  selectedCategory?: string;
  setSelectedCategory?: (category: string) => void;
  viewMode?: 'grid' | 'list';
  setViewMode?: (mode: 'grid' | 'list') => void;
  onClearFilters?: () => void;
  hasActiveFilters?: boolean;
}

const MobileHeaderContainer = styled(motion.header)<{ $isMinimized: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(220, 38, 38, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${props => props.$isMinimized ? "translateY(-100%)" : "translateY(0)"};
`;

const MobileNav = styled.nav<{ $isRTL: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const MobileBrandContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:active {
    transform: scale(0.95);
  }
`;

const MobileLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  
  img {
    height: 100%;
    width: 100%;
    object-fit: contain;
  }
`;

const MobileTitle = styled.div<{ $isRTL: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  height: 35px;
  
  img {
    height: 100%;
    width: auto;
    object-fit: contain;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

const MobileMenuButton = styled.button<{ $isOpen: boolean; $isRTL?: boolean }>`
  background: ${props => props.$isOpen ? 'linear-gradient(135deg, #0f172a 0%, #dc2626 100%)' : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'};
  border: 1px solid rgba(220, 38, 38, 0.1);
  border-radius: 12px;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  
  &:active {
    transform: scale(0.95);
  }
`;

const MobileHamburgerLine = styled.span<{ $isOpen: boolean }>`
  width: 20px;
  height: 2px;
  background: ${props => props.$isOpen ? '#ffffff' : '#dc2626'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center;
  
  &:nth-child(1) {
    transform: ${props => props.$isOpen ? 'rotate(45deg) translate(5px, 5px)' : 'rotate(0)'};
  }
  
  &:nth-child(2) {
    opacity: ${props => props.$isOpen ? '0' : '1'};
    transform: ${props => props.$isOpen ? 'scale(0)' : 'scale(1)'};
  }
  
  &:nth-child(3) {
    transform: ${props => props.$isOpen ? 'rotate(-45deg) translate(7px, -6px)' : 'rotate(0)'};
  }
`;

const MobileMenuOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  z-index: 999;
`;

const MobileMenuContainer = styled(motion.div)<{ $isRTL: boolean }>`
  position: fixed;
  top: 0;
  ${props => props.$isRTL ? 'right: 0;' : 'left: 0;'}
  width: 320px;
  height: 100vh;
  background: white;
  box-shadow: ${props => props.$isRTL ? 
    '-8px 0 32px rgba(0, 0, 0, 0.2)' : 
    '8px 0 32px rgba(0, 0, 0, 0.2)'};
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const MobileMenuContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem 1.5rem;
`;

const MobileMenuFooter = styled.div<{ $isRTL: boolean }>`
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const SocialTitle = styled.h4<{ $isRTL: boolean }>`
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 1rem 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
`;

const SocialButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const SocialButton = styled(motion.a)<{ $isRTL: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  border: 2px solid rgba(220, 38, 38, 0.3);
  background: rgba(255, 255, 255, 0.9);
  
  img {
    width: 24px;
    height: 24px;
    filter: brightness(0) saturate(100%) invert(17%) sepia(94%) saturate(2371%) hue-rotate(346deg) brightness(91%) contrast(93%);
    transition: filter 0.3s ease;
  }
  
  &:hover {
    border-color: #dc2626;
    background: rgba(220, 38, 38, 0.05);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.2);
    
    img {
      filter: brightness(0) saturate(100%) invert(17%) sepia(94%) saturate(2371%) hue-rotate(346deg) brightness(91%) contrast(93%);
    }
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const MobileMenuHeader = styled.div<{ $isRTL: boolean }>`
  text-align: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const MobileMenuTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const MobileNavLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const MobileNavLink = styled.div<{ $isActive?: boolean; $isRTL?: boolean }>`
  padding: 1.25rem 1.5rem;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: ${props => props.$isActive ? 'rgba(220, 38, 38, 0.1)' : 'transparent'};
  color: ${props => props.$isActive ? '#dc2626' : '#374151'};
  font-weight: ${props => props.$isActive ? '600' : '500'};
  font-size: 1.1rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  border: 2px solid ${props => props.$isActive ? 'rgba(220, 38, 38, 0.2)' : 'transparent'};
  
  &:active {
    transform: scale(0.98);
    background: rgba(220, 38, 38, 0.1);
    color: #dc2626;
  }
`;

const MobileSearchSection = styled.div<{ $isRTL: boolean; $isMinimized: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  margin-top: 1rem;
  margin-bottom: 1rem;
  padding: 1rem;
  border-top: 1px solid rgba(220, 38, 38, 0.1);
  max-height: ${props => props.$isMinimized ? '0' : '300px'};
  overflow: hidden;
  opacity: ${props => props.$isMinimized ? '0' : '1'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const MobileSearchInput = styled.input<{ $isRTL: boolean }>`
  padding: 1rem 1.25rem;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  font-size: 1.1rem;
  width: 100%;
  background: #f8fafc;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:focus {
    outline: none;
    border-color: #dc2626;
    background: white;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  }
  
  &::placeholder {
    color: #94a3b8;
  }
`;

const MobileFiltersRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MobileCategoryTabs = styled.div`
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0.5rem 0;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const MobileCategoryTab = styled(motion.button)<{ $isActive: boolean; $isRTL: boolean }>`
  padding: 0.75rem 1.25rem;
  border: 2px solid ${props => props.$isActive ? '#dc2626' : '#e2e8f0'};
  background: ${props => props.$isActive ? '#dc2626' : 'white'};
  color: ${props => props.$isActive ? 'white' : '#64748b'};
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.95rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  white-space: nowrap;
  flex-shrink: 0;
  
  &:active {
    transform: scale(0.95);
  }
`;

const MobileViewToggle = styled.div`
  display: flex;
  gap: 0.5rem;
  background: #f1f5f9;
  padding: 0.5rem;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  align-self: center;
`;

const MobileViewButton = styled.button<{ $isActive: boolean }>`
  padding: 0.75rem 1rem;
  border: none;
  background: ${props => props.$isActive ? '#dc2626' : 'transparent'};
  color: ${props => props.$isActive ? 'white' : '#64748b'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 1rem;
  font-weight: 600;
  
  &:active {
    transform: scale(0.95);
  }
`;

const MobileClearFiltersButton = styled(motion.button)<{ $isRTL: boolean }>`
  padding: 0.75rem 1.25rem;
  border: 2px solid #6b7280;
  background: white;
  color: #6b7280;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.95rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  align-self: center;
  
  &:active {
    transform: scale(0.95);
  }
`;


const MobileLanguageIcon = styled.button<{ $isRTL: boolean }>`
  width: 40px;
  height: 40px;
  border: 2px solid rgba(220, 38, 38, 0.3);
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  font-weight: 700;
  font-size: 0.9rem;
  color: #dc2626;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  position: relative;
  overflow: hidden;
  
  &:active {
    transform: scale(0.95);
    background: rgba(220, 38, 38, 0.1);
    border-color: #dc2626;
  }
  
  &:hover {
    background: rgba(220, 38, 38, 0.1);
    border-color: #dc2626;
    transform: translateY(-1px);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(185, 28, 28, 0.1) 100%);
    border-radius: 50%;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
`;

const HeaderMobile: React.FC<HeaderMobileProps> = ({ 
  language, 
  setLanguage, 
  t, 
  isRTL, 
  showOnlyHome = false,
  searchTerm,
  setSearchTerm,
  selectedCategory,
  setSelectedCategory,
  viewMode,
  setViewMode,
  onClearFilters,
  hasActiveFilters
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHeaderMinimized, setIsHeaderMinimized] = useState(false);
  const [isSearchMinimized, setIsSearchMinimized] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const lastScrollY = useRef(0);
  const languageDropdownRef = useRef<HTMLDivElement>(null);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const navigate = useNavigate();
  const location = useLocation();

  const isProductsPage = location.pathname === '/products';
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    // Route-based active section detection
    const pathname = location.pathname;
    
    if (pathname === '/solaire' || pathname === '/installation-service') {
      setActiveSection('services-mobile');
    } else if (pathname === '/products' || pathname.startsWith('/category/') || pathname.startsWith('/product/')) {
      setActiveSection('products');
    } else if (pathname === '/about') {
      setActiveSection('about');
    } else if (pathname === '/markets') {
      setActiveSection('markets');
    } else if (pathname === '/partners') {
      setActiveSection('partners');
    } else if (pathname === '/contact') {
      setActiveSection('contact');
    } else if (pathname === '/') {
      // Only use scroll detection on home page
    }
  }, [location.pathname]);

  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;
    
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Mobile header minimization logic
      if (currentScrollY > lastScrollY.current && currentScrollY > 10) {
        // Scrolling down - minimize entire header
        setIsHeaderMinimized(true);
        if (isProductsPage) {
          setIsSearchMinimized(true);
        }
      } else if (currentScrollY < lastScrollY.current) {
        // Scrolling up - expand header
        setIsHeaderMinimized(false);
        if (isProductsPage) {
          setIsSearchMinimized(false);
        }
      }
      
      lastScrollY.current = currentScrollY;
      setIsScrolled(window.scrollY > 50);
      
      // Only use scroll detection on home page
      if (location.pathname === '/') {
        // Debounce scroll detection for better performance
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          if (!showOnlyHome) {
                      // Enhanced scroll detection with priority for special sections
          const scrollPosition = window.scrollY + 200;
          
          let currentSection = 'home'; // Default fallback
          
          // Priority 1: Check for discover-products section
          const discoverProductsSection = document.getElementById('discover-products');
          if (discoverProductsSection) {
            const discoverTop = discoverProductsSection.offsetTop;
            const discoverBottom = discoverTop + discoverProductsSection.offsetHeight;
            if (scrollPosition >= (discoverTop - 200) && scrollPosition < (discoverBottom + 200)) {
              currentSection = 'discover-products';
            }
          }
          
          // Priority 2: Check for stats section (part of partners)
          if (currentSection === 'home') {
            const statsSection = document.getElementById('stats');
            if (statsSection) {
              const statsTop = statsSection.offsetTop;
              const statsBottom = statsTop + statsSection.offsetHeight;
              if (scrollPosition >= (statsTop - 200) && scrollPosition < (statsBottom + 200)) {
                currentSection = 'partners';
              }
            }
          }
          
          // Priority 3: Check for trusted-clients section (part of partners)
          if (currentSection === 'home') {
            const trustedClientsSection = document.getElementById('trusted-clients');
            if (trustedClientsSection) {
              const trustedTop = trustedClientsSection.offsetTop;
              const trustedBottom = trustedTop + trustedClientsSection.offsetHeight;
              if (scrollPosition >= (trustedTop - 200) && scrollPosition < (trustedBottom + 200)) {
                currentSection = 'partners';
              }
            }
          }
          
          // Priority 4: Check for regular sections only if no special section was found
          if (currentSection === 'home') {
            const sections = ['home', 'about', 'services-mobile', 'markets', 'partners', 'contact'];
            for (let i = 0; i < sections.length; i++) {
              const section = document.getElementById(sections[i]);
              if (section) {
                const sectionTop = section.offsetTop;
                const sectionBottom = sectionTop + section.offsetHeight;
                
                // Check if the scroll position is within this section
                if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
                  currentSection = sections[i];
                  break;
                }
                // If we are past the last section, use the last section
                if (i === sections.length - 1 && scrollPosition >= sectionTop) {
                  currentSection = sections[i];
                }
              }
            }
          }
          
          setActiveSection(currentSection);
          }
        }, 10);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial call to set active section on mount
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [showOnlyHome, isProductsPage, location.pathname]);

  const handleNavClick = (sectionId: string) => {
    if (showOnlyHome) {
      navigate('/');
    } else {
      scrollToSection(sectionId);
      setActiveSection(sectionId);
    }
    setIsMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    if (showOnlyHome) {
      navigate('/');
    } else {
      scrollToSection('home');
    }
  };

  const handleSearchTap = () => {
    if (isSearchMinimized) {
      setIsSearchMinimized(false);
    }
  };

  const handleHomeClick = () => {
    navigate('/');
  };

  const handleLanguageChange = (newLanguage: 'fr' | 'en' | 'ar') => {
    setLanguage(newLanguage);
    localStorage.setItem('techyid-language', newLanguage);
    setIsLanguageMenuOpen(false);
  };

  const getLanguageDisplay = () => {
    switch (language) {
      case 'fr': return 'FR';
      case 'en': return 'EN';
      case 'ar': return 'ع';
      default: return 'FR';
    }
  };

  const getLanguageButtons = () => {
    const languages: ('fr' | 'en' | 'ar')[] = ['fr', 'en', 'ar'];
    
    if (!isLanguageMenuOpen) {
      // Show only current language button
      return [
        <MobileLanguageIcon
          key={language}
          $isRTL={isRTL}
          onClick={() => setIsLanguageMenuOpen(true)}
          aria-label={`Select language. Current: ${language.toUpperCase()}`}
        >
          {getLanguageDisplay()}
        </MobileLanguageIcon>
      ];
    }
    
    // Show all language buttons
    return languages.map((lang) => (
      <MobileLanguageIcon
        key={lang}
        $isRTL={isRTL}
        onClick={() => handleLanguageChange(lang)}
        aria-label={`Switch to ${lang.toUpperCase()}`}
      >
        {lang === 'fr' ? 'FR' : lang === 'en' ? 'EN' : 'ع'}
      </MobileLanguageIcon>
    ));
  };

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setIsLanguageMenuOpen(false);
      }
    };

    if (isLanguageMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLanguageMenuOpen]);


  const navItems = showOnlyHome 
    ? [{ id: 'home', label: t.home }]
    : [
        { id: 'home', label: t.home },
        { id: 'about', label: t.about },
        { id: 'discover-products', label: t.products },
        { id: 'services-mobile', label: t.services },
        { id: 'markets', label: t.markets },
        { id: 'partners', label: t.partners },
        { id: 'contact', label: t.contact },
      ];

  const categories = [
    { id: '0', label: t.materialEquipment || 'Material & Equipment' },
    { id: '1', label: t.groupesElectrogenes || 'Groupes Electrogènes' },
    { id: '2', label: t.accessoriesElectriques || 'Accessories Electriques' },
  ];

  return (
    <>
      <MobileHeaderContainer 
        $isMinimized={isHeaderMinimized}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        style={{
          background: isScrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.95)',
        }}
      >
        <MobileNav $isRTL={isRTL}>
          <MobileBrandContainer onClick={handleLogoClick}>
            <MobileLogo>
              <img src="/images/logo192.png" alt={t.altTexts?.logo || 'Logo'} />
            </MobileLogo>
            <MobileTitle $isRTL={isRTL}>
              <img src={language === 'ar' ? "/images/textar.png" : "/images/text.png"} alt="TECHYID" />
            </MobileTitle>
          </MobileBrandContainer>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div ref={languageDropdownRef} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              {getLanguageButtons()}
            </div>
            
            <MobileMenuButton 
              $isOpen={isMobileMenuOpen} 
              $isRTL={isRTL} 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={t.accessibility?.toggleMobileMenu || 'Toggle menu'}
            >
              <MobileHamburgerLine $isOpen={isMobileMenuOpen} />
              <MobileHamburgerLine $isOpen={isMobileMenuOpen} />
              <MobileHamburgerLine $isOpen={isMobileMenuOpen} />
            </MobileMenuButton>
          </div>
        </MobileNav>

        {isProductsPage && searchTerm !== undefined && (
          <MobileSearchSection $isRTL={isRTL} $isMinimized={isSearchMinimized} onTouchStart={handleSearchTap}>
            <MobileSearchInput
              $isRTL={isRTL}
              type="text"
              placeholder={t.searchProducts || 'Rechercher des produits...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm?.(e.target.value)}
            />
            
            <MobileFiltersRow>
              <MobileCategoryTabs>
                {categories.map((category) => (
                  <MobileCategoryTab
                    key={category.id}
                    $isActive={selectedCategory === category.id}
                    $isRTL={isRTL}
                    onClick={() => setSelectedCategory?.(selectedCategory === category.id ? '' : category.id)}
                    whileTap={{ scale: 0.95 }}
                  >
                    {category.label}
                  </MobileCategoryTab>
                ))}
              </MobileCategoryTabs>
              
              <MobileViewToggle>
                <MobileViewButton 
                  $isActive={viewMode === 'grid'} 
                  onClick={() => setViewMode?.('grid')}
                >
                  ⚏
                </MobileViewButton>
                <MobileViewButton 
                  $isActive={viewMode === 'list'} 
                  onClick={() => setViewMode?.('list')}
                >
                  ☰
                </MobileViewButton>
              </MobileViewToggle>
              
              {hasActiveFilters && onClearFilters && (
                <MobileClearFiltersButton
                  $isRTL={isRTL}
                  onClick={onClearFilters}
                  whileTap={{ scale: 0.95 }}
                >
                  ✕ {t.clearFilters || 'Effacer'}
                </MobileClearFiltersButton>
              )}
            </MobileFiltersRow>
          </MobileSearchSection>
        )}
      </MobileHeaderContainer>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileMenuOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <MobileMenuContainer
              $isRTL={isRTL}
              initial={{ 
                x: isRTL ? 320 : -320, 
                opacity: 0 
              }}
              animate={{ 
                x: 0, 
                opacity: 1 
              }}
              exit={{ 
                x: isRTL ? 320 : -320, 
                opacity: 0 
              }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              <MobileMenuContent>
                <MobileMenuHeader $isRTL={isRTL}>
                  <MobileMenuTitle $isRTL={isRTL}>{t.navigation || 'Navigation'}</MobileMenuTitle>
                </MobileMenuHeader>
                
                <MobileNavLinks>
                  {navItems.map((item) => (
                    <MobileNavLink
                      key={item.id}
                      $isActive={activeSection === item.id}
                      $isRTL={isRTL}
                      onClick={() => handleNavClick(item.id)}
                    >
                      {item.label}
                    </MobileNavLink>
                  ))}
                </MobileNavLinks>
              </MobileMenuContent>
              
              <MobileMenuFooter $isRTL={isRTL}>
                <SocialTitle $isRTL={isRTL}>
                  {t.followUs || 'Suivez-nous'}
                </SocialTitle>
                <SocialButtons>
                  <SocialButton
                    $isRTL={isRTL}
                    href="https://www.facebook.com/profile.php?id=61578814623072"
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label="Facebook"
                  >
                    <img src="/images/drawer/facebook.svg" alt="Facebook" />
                  </SocialButton>
                  <SocialButton
                    $isRTL={isRTL}
                    href="https://wa.me/22248610689"
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label="WhatsApp"
                  >
                    <img src="/images/drawer/whatsapp.svg" alt="WhatsApp" />
                  </SocialButton>
                </SocialButtons>
              </MobileMenuFooter>
            </MobileMenuContainer>
          </MobileMenuOverlay>
        )}
      </AnimatePresence>
    </>
  );
};

export default HeaderMobile;
